import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Eye,
  ImageIcon,
  Layers,
  Loader2,
  Palette,
  Plus,
  Sparkles,
  Video,
  Wand2,
  X,
} from "lucide-react";
import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useActiveContext } from "../../contexts/ActiveContextContext";
import { useTranslation } from "../../contexts/LanguageContext";
import postsService from "../../services/postsService";
import apiService from "../../shared/apiService";
import { showConfirm } from "../../shared/alertService";
import {
  IMAGE_PROVIDER_OPTIONS,
  imageProviderHint,
} from "../../shared/imageProviders";
import VideoGenerationFields from "../video/VideoGenerationFields";
import { VideoTextItem } from "../../utils/videoGenerationRequest";
import { cn } from "../../utils/cn";
import {
  getPlatformConfig,
  getPlatformName,
  PLATFORMS_LIST,
} from "../../utils/platformIcons";
import { getStrictestCaptionLimit } from "../../utils/postCaptionLimits";
import { getStrictestHashtagLimit } from "../../utils/postHashtagLimits";
import Button from "../ui/Button";
import CharCounter from "../ui/CharCounter";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/Tabs";
import Textarea from "../ui/Textarea";
import { FORMAT_OPTIONS as CREATIVE_FORMAT_OPTIONS } from "./CampaignCreativeEditor";
import SocialVariationsModal from "./SocialVariationsModal";

// Gatilhos emocionais suportados pelo Creative AI (ai/tools/content_generator.py::EMOTIONAL_TRIGGERS)
const EMOTIONAL_TRIGGER_OPTIONS = [
  { value: "", label: "Deixe a IA decidir" },
  { value: "fomo", label: "FOMO (medo de ficar de fora)" },
  { value: "curiosidade", label: "Curiosidade" },
  { value: "controversia", label: "Controvérsia" },
  { value: "identidade", label: "Identidade / pertencimento" },
  { value: "autoridade", label: "Autoridade" },
  { value: "prova_social", label: "Prova social" },
  { value: "urgencia", label: "Urgência" },
];

const VIDEO_DESTINATION_OPTIONS = [
  { value: "instagram_feed", label: "Instagram Feed" },
  { value: "instagram_reels", label: "Instagram Reels" },
  { value: "facebook_reels", label: "Facebook Reels" },
  { value: "linkedin_feed", label: "LinkedIn (Feed/Página)" },
  { value: "facebook_page", label: "Facebook (Página - Feed)" },
];

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (postData: {
    content: string;
    platforms: string[];
    hashtags?: string;
    call_to_action?: string;
    call_to_action_url?: string;
    imageFile?: File;
    videoFile?: File;
    videoDestination?: string;
    videoCaption?: string;
    videoHashtags?: string;
    generateImage?: boolean;
    imagePrompt?: string;
    imageProvider?: string;
    generateVideo?: boolean;
    videoPrompt?: string;
    videoDuration?: number;
    videoProvider?: string;
    videoRestrictions?: string;
    videoLogoFile?: File | null;
    videoReferenceImages?: File[];
    videoTexts?: VideoTextItem[];
    videoScript?: string;
    enhanceWithAI?: boolean;
    tempPostId?: number;
    generatedImageUrl?: string;
    mediaGallery?: { file: File; type: "image" | "video" }[];
  }) => Promise<void>;
  loading?: boolean;
  /** Chamado após o Creative AI (briefing/variações) criar post(s) diretamente no backend,
   * pra o chamador atualizar a lista — esse fluxo não passa por onCreate. */
  onCreativeGenerated?: () => void;
}

export const PLATFORMS = PLATFORMS_LIST.map((platform: any) => ({
  id: platform.id,
  name: platform.name,
  icon: <platform.icon className="h-4 w-4" />,
}));

const CreatePostModal: React.FC<CreatePostModalProps> = ({
  open,
  onClose,
  onCreate,
  loading = false,
  onCreativeGenerated,
}) => {
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();
  const previewAccountName =
    activeContext.clientCompany?.name ||
    activeContext.organization?.name ||
    t("Sua Empresa");
  const previewAccountLogo =
    activeContext.clientCompany?.logo_url ||
    activeContext.organization?.logo_url ||
    null;
  const hasBrandKit = Boolean(
    activeContext.clientCompany?.primary_color ||
    activeContext.clientCompany?.background_color ||
    activeContext.organization?.primary_color ||
    activeContext.organization?.background_color,
  );
  // Estados básicos
  const [content, setContent] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([
    "linkedin",
  ]);
  const [hashtags, setHashtags] = useState("");
  const [hashtagsArray, setHashtagsArray] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState("");
  const [callToAction, setCallToAction] = useState("");
  const [callToActionUrl, setCallToActionUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Vira `true` na primeira tentativa de envio com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Estados para imagem
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [generateImage, setGenerateImage] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageProvider, setImageProvider] = useState("dalle");
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const [tempPostId, setTempPostId] = useState<number | null>(null);

  // Estados para vídeo
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoDestination, setVideoDestination] = useState("instagram_feed");
  const [videoCaption, setVideoCaption] = useState("");
  const [videoHashtags, setVideoHashtags] = useState("");
  const [generateVideo, setGenerateVideo] = useState(false);
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoProvider, setVideoProvider] = useState("veo");
  const [videoRestrictions, setVideoRestrictions] = useState("");
  const [videoLogoFile, setVideoLogoFile] = useState<File | null>(null);
  const [videoLogoPreview, setVideoLogoPreview] = useState<string | null>(null);
  const [videoReferenceImages, setVideoReferenceImages] = useState<File[]>([]);
  const [videoReferencePreviews, setVideoReferencePreviews] = useState<
    string[]
  >([]);
  const [videoTexts, setVideoTexts] = useState<VideoTextItem[]>([]);
  const [videoScript, setVideoScript] = useState("");

  // Estados para o Creative AI (briefing simples: estratégia -> copy -> criativo -> revisor)
  const [briefPanelOpen, setBriefPanelOpen] = useState(false);
  const [briefTopic, setBriefTopic] = useState("");
  const [briefProduct, setBriefProduct] = useState("");
  const [briefObjective, setBriefObjective] = useState("");
  const [briefTrigger, setBriefTrigger] = useState("");
  const [briefFormat, setBriefFormat] = useState("instagram_feed");
  const [briefVariationsCount, setBriefVariationsCount] = useState(3);
  const [briefSubmitting, setBriefSubmitting] = useState<
    "post" | "variations" | null
  >(null);
  const [briefImageStatus, setBriefImageStatus] = useState<
    "idle" | "generating" | "done" | "failed"
  >("idle");
  const [briefError, setBriefError] = useState<string | null>(null);
  const [briefResult, setBriefResult] = useState<{
    postId: number;
    reviewFlags: string[];
  } | null>(null);
  const [briefVariationsResult, setBriefVariationsResult] = useState<{
    conceptId: string;
    createdCount: number;
    total: number;
    reviewFlags: string[];
  } | null>(null);

  // Estado para galeria (carrossel/álbum)
  const [galleryItems, setGalleryItems] = useState<
    {
      file: File;
      preview: string;
      type: "image" | "video";
      variations?: Record<string, { public_url: string }>;
    }[]
  >([]);

  // Estado para melhorar com IA
  const [enhanceWithAI, setEnhanceWithAI] = useState(false);
  const [enhancing, setEnhancing] = useState(false);

  // Estado para variações sociais
  const [variationsModalOpen, setVariationsModalOpen] = useState(false);
  const [selectedItemForVariations, setSelectedItemForVariations] = useState<{
    variations: Record<string, { public_url: string }>;
    originalUrl: string;
  } | null>(null);

  // Estado para tabs
  const [activeTab, setActiveTab] = useState("media");

  // Plataforma exibida na aba de pré-visualização (padrão: primeira selecionada)
  const [previewPlatformOverride, setPreviewPlatformOverride] = useState<
    string | null
  >(null);
  const previewPlatform =
    previewPlatformOverride &&
    selectedPlatforms.includes(previewPlatformOverride)
      ? previewPlatformOverride
      : selectedPlatforms[0];

  // Funções para gerenciar hashtags
  const addHashtag = (tag: string) => {
    const cleanedTag = tag.trim().replace(/^#+/, ""); // Remove # do início se existir
    if (cleanedTag && !hashtagsArray.includes(cleanedTag)) {
      const newArray = [...hashtagsArray, cleanedTag];
      setHashtagsArray(newArray);
      setHashtags(newArray.join(" "));
      setHashtagInput("");
    }
  };

  const removeHashtag = (tagToRemove: string) => {
    const newArray = hashtagsArray.filter((tag) => tag !== tagToRemove);
    setHashtagsArray(newArray);
    setHashtags(newArray.join(" "));
  };

  const handleHashtagInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (hashtagInput.trim()) {
        addHashtag(hashtagInput);
      }
    }
  };

  const handleHashtagInputBlur = () => {
    if (hashtagInput.trim()) {
      addHashtag(hashtagInput);
    }
  };

  useEffect(() => {
    if (open) {
      // Check if there's content from Viral Research in localStorage
      const viralContent = localStorage.getItem("viral_research_content");
      if (viralContent) {
        setContent(viralContent);
        localStorage.removeItem("viral_research_content");
      } else {
        setContent("");
      }
      setSelectedPlatforms(["linkedin"]);
      setHashtags("");
      setHashtagsArray([]);
      setHashtagInput("");
      setCallToAction("");
      setError(null);
      setAttemptedSubmit(false);
      setIsSubmitting(false);
      setImageFile(null);
      setGenerateImage(false);
      setImagePrompt("");
      setGeneratingImage(false);
      setGeneratedImageUrl(null);
      setTempPostId(null);
      setVideoFile(null);
      setVideoDestination("instagram_feed");
      setVideoCaption("");
      setVideoHashtags("");
      setGenerateVideo(false);
      setVideoPrompt("");
      setVideoDuration(8);
      setVideoProvider("veo");
      setVideoRestrictions("");
      setVideoLogoFile(null);
      setVideoLogoPreview(null);
      setVideoReferenceImages([]);
      setVideoReferencePreviews([]);
      setVideoTexts([]);
      setVideoScript("");
      setEnhanceWithAI(false);
      setGalleryItems([]);
      setActiveTab("media");
      setPreviewPlatformOverride(null);
      setBriefPanelOpen(false);
      setBriefTopic("");
      setBriefProduct("");
      setBriefObjective("");
      setBriefTrigger("");
      setBriefFormat("instagram_feed");
      setBriefVariationsCount(3);
      setBriefSubmitting(null);
      setBriefImageStatus("idle");
      setBriefError(null);
      setBriefResult(null);
      setBriefVariationsResult(null);
    }
  }, [open]);

  useEffect(() => {
    const handleViralContent = (e: any) => {
      if (e.detail) {
        setContent(e.detail);
      }
    };
    window.addEventListener("viral_research_content_ready", handleViralContent);
    return () => {
      window.removeEventListener(
        "viral_research_content_ready",
        handleViralContent,
      );
    };
  }, []);

  // Combina o limite mais restritivo entre as plataformas selecionadas —
  // é só um indicativo (nenhuma plataforma é bloqueada por isso). Sem
  // nenhuma plataforma marcada, cai para o limite geral (todas as
  // plataformas) em vez de sumir o contador.
  const captionLimit = useMemo(
    () =>
      getStrictestCaptionLimit(
        selectedPlatforms.length > 0
          ? selectedPlatforms
          : PLATFORMS.map((p: any) => p.id),
      ),
    [selectedPlatforms],
  );

  const hashtagLimit = useMemo(
    () => getStrictestHashtagLimit(selectedPlatforms),
    [selectedPlatforms],
  );

  const handlePlatformToggle = (platformId: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformId)
        ? prev.filter((id) => id !== platformId)
        : [...prev, platformId],
    );
  };

  const removeItem = (index: number) => {
    setGalleryItems((prev) => prev.filter((_, i) => i !== index));
  };

  const openVariations = (index: number) => {
    const item = galleryItems[index];
    if (item.variations) {
      setSelectedItemForVariations({
        variations: item.variations,
        originalUrl: item.preview,
      });
      setVariationsModalOpen(true);
    }
  };

  const handleGenerateImagePreview = async () => {
    if (!imagePrompt.trim()) {
      setError(t("Digite um prompt para gerar a imagem"));
      return;
    }

    if (!content.trim()) {
      setError(t("Digite o conteúdo do post antes de gerar a imagem"));
      return;
    }

    if (selectedPlatforms.length === 0) {
      setError(
        t("Selecione pelo menos uma plataforma antes de gerar a imagem"),
      );
      return;
    }

    setGeneratingImage(true);
    setError(null);

    try {
      // Criar um post temporário para gerar a imagem
      const platform = selectedPlatforms[0]; // Usar a primeira plataforma selecionada

      // Preparar dados do post - o backend espera hashtags como string, não array
      const postData: any = {
        content: content.trim(),
        platform,
      };

      // Converter hashtags array para string (formato esperado pelo backend)
      if (hashtagsArray && hashtagsArray.length > 0) {
        postData.hashtags = hashtagsArray.join(", ");
      } else if (hashtags.trim()) {
        // Se não houver array mas houver string, usar a string
        postData.hashtags = hashtags.trim();
      }

      // Adicionar call_to_action e call_to_action_url apenas se houver
      if (callToAction.trim()) {
        postData.call_to_action = callToAction.trim();
      }
      if (callToActionUrl.trim()) {
        postData.call_to_action_url = callToActionUrl.trim();
      }

      const result = await postsService.createPost(postData);

      console.log("📝 Resultado do createPost:", result);

      // O backend retorna PostResponse.from_domain(post).dict() diretamente
      // que é um objeto com id, user_id, content, etc.
      // Mas o apiService pode envolver em algum formato
      // Verificar diferentes formatos possíveis de resposta
      let postId: number | null = null;
      let postData_result: any = null;

      // Tentar diferentes formatos de resposta
      if (result?.id) {
        // Formato direto: { id: 1, user_id: 1, ... }
        postId = result.id;
        postData_result = result;
      } else if (result?.post?.id) {
        // Formato aninhado: { post: { id: 1, ... } }
        postId = result.post.id;
        postData_result = result.post;
      } else if (result?.data?.id) {
        // Formato com data: { data: { id: 1, ... } }
        postId = result.data.id;
        postData_result = result.data;
      } else if (typeof result === "object" && "id" in result) {
        // Último recurso: verificar se tem id diretamente
        postId = (result as any).id;
        postData_result = result;
      }

      if (!postId) {
        console.error("❌ Estrutura de resposta inesperada:", result);
        throw new Error(
          t(
            "Erro ao criar post temporário: ID do post não encontrado na resposta. Resposta recebida: {response}",
            { response: JSON.stringify(result) },
          ),
        );
      }

      console.log("✅ Post criado com ID:", postId, "Dados:", postData_result);
      setTempPostId(postId);

      // Gerar a imagem
      const generateResult = await postsService.generateImageAndWait(postId, {
        prompt: imagePrompt.trim(),
        provider: imageProvider,
      });

      if (!generateResult?.success) {
        const errorMsg =
          generateResult?.error ||
          generateResult?.message ||
          t("Erro desconhecido ao gerar imagem");
        throw new Error(String(errorMsg));
      }

      // Obter a URL da imagem gerada
      const imageInfo =
        (generateResult as any).image_info ||
        (generateResult.post as any)?.image_info;
      if (imageInfo?.public_url) {
        const imageUrl = imageInfo.public_url.startsWith("http")
          ? imageInfo.public_url
          : `${window.location.origin}${imageInfo.public_url}`;
        setGeneratedImageUrl(imageUrl);
        // Adicionar imagem gerada à galeria com variações
        setGalleryItems((prev) => [
          ...prev,
          {
            file: new File([], "IA_Generated.png"),
            preview: imageUrl,
            type: "image",
            variations: imageInfo.variations,
          },
        ]);
        console.log("✅ Imagem gerada com sucesso! URL:", imageUrl);
        console.log(
          "✅ Post temporário ID:",
          postId,
          "está pronto para ser usado",
        );
      } else {
        console.warn(
          "⚠️ Imagem gerada mas image_info não encontrado:",
          generateResult,
        );
        // Mesmo sem URL, a imagem foi gerada e está no post
        // Continuar o fluxo
        setGeneratedImageUrl("generated"); // Marcar como gerada
      }
    } catch (err: any) {
      console.error("❌ Erro ao gerar imagem:", err);

      // Extrair mensagem de erro mais detalhada
      let errorMessage = t("Erro ao gerar imagem com IA");

      if (err?.message) {
        errorMessage = err.message;
        // Se a mensagem contém detalhes entre parênteses, manter
        if (errorMessage.includes("(Detalhes:")) {
          // Já tem detalhes, usar como está
        } else if (err?.errorData) {
          // Adicionar detalhes se disponíveis
          if (err.errorData.details) {
            const details =
              typeof err.errorData.details === "string"
                ? err.errorData.details
                : JSON.stringify(err.errorData.details);
            errorMessage = `${errorMessage} ${t("(Detalhes: {details})", { details })}`;
          }
        }
      } else if (err?.error) {
        errorMessage = err.error;
      } else if (err?.errorData) {
        // Se houver detalhes de validação, mostrar
        if (err.errorData.details && Array.isArray(err.errorData.details)) {
          const validationErrors = err.errorData.details
            .map((detail: any) => {
              const field = detail.loc?.join(".") || detail.field || t("campo");
              const msg = detail.msg || detail.message || t("inválido");
              return `${field}: ${msg}`;
            })
            .join(", ");
          errorMessage = t("Erro de validação: {errors}", {
            errors: validationErrors,
          });
        } else {
          errorMessage =
            err.errorData.error || err.errorData.message || errorMessage;
          // Adicionar detalhes se disponíveis
          if (err.errorData.details) {
            const details =
              typeof err.errorData.details === "string"
                ? err.errorData.details
                : JSON.stringify(err.errorData.details);
            errorMessage = `${errorMessage} ${t("(Detalhes: {details})", { details })}`;
          }
        }
      }

      setError(errorMessage);

      // Se criou um post temporário, tentar deletá-lo
      if (tempPostId) {
        try {
          await postsService.deletePost(tempPostId);
        } catch (deleteErr) {
          console.error("Erro ao deletar post temporário:", deleteErr);
        }
        setTempPostId(null);
      }
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleEnhanceContent = async () => {
    if (!content.trim()) {
      setError(t("Escreva algum conteúdo antes de melhorar com IA"));
      return;
    }

    const confirmed = await showConfirm(
      t(
        "A IA vai reescrever sua legenda atual (e pode ajustar CTA e hashtags). O texto atual será substituído.",
      ),
      t("Melhorar com IA?"),
      t("Melhorar"),
      t("Cancelar"),
    );
    if (!confirmed) return;

    setEnhancing(true);
    setError(null);
    try {
      const platform = selectedPlatforms[0] || "linkedin";
      const result = await apiService.post<any>("/api/integration/enhance", {
        post_content: content.trim(),
        platform: platform,
      });

      if (result && (result.success || (result as any).data?.enhanced_post)) {
        // Obter os dados corretos dependendo de como a apiService encapsula a resposta
        const responseData = result.data || result;
        const enhanced = responseData.enhanced_post;

        if (enhanced) {
          if (enhanced.text) {
            setContent(enhanced.text);
          }
          if (enhanced.call_to_action) {
            setCallToAction(enhanced.call_to_action);
          }
          if (enhanced.hashtags) {
            let tagsList: string[] = [];
            if (Array.isArray(enhanced.hashtags)) {
              tagsList = enhanced.hashtags.map((tag: string) =>
                tag.trim().replace(/^#+/, ""),
              );
            } else if (typeof enhanced.hashtags === "string") {
              tagsList = enhanced.hashtags
                .split(/[ ,]+/)
                .map((tag: string) => tag.trim().replace(/^#+/, ""))
                .filter(Boolean);
            }
            setHashtagsArray(tagsList);
            setHashtags(tagsList.join(" "));
          }
          setEnhanceWithAI(true);
        } else {
          throw new Error(t("A IA retornou um formato de resposta vazio."));
        }
      } else {
        throw new Error(
          result?.error || t("Falha ao processar otimização do post com IA"),
        );
      }
    } catch (err: any) {
      console.error("❌ Erro ao melhorar post:", err);
      setError(
        err?.message ||
          t("Erro ao melhorar conteúdo com IA. Tente novamente mais tarde."),
      );
    } finally {
      setEnhancing(false);
    }
  };

  const handleGenerateFromBrief = async () => {
    if (!briefTopic.trim()) {
      setBriefError(t("Digite o tema do post"));
      return;
    }

    setBriefError(null);
    setBriefResult(null);
    setBriefSubmitting("post");
    setBriefImageStatus("idle");
    try {
      const platform = selectedPlatforms[0] || "instagram";
      const res = await postsService.generateFromBrief({
        topic: briefTopic.trim(),
        product: briefProduct.trim() || undefined,
        objective: briefObjective.trim() || undefined,
        platform,
        format: briefFormat,
      });

      if (!res?.success || !res.post_id) {
        throw new Error(res?.error || t("Erro ao gerar post com Creative AI"));
      }

      setBriefResult({
        postId: res.post_id,
        reviewFlags: res.review_flags || [],
      });
      onCreativeGenerated?.();

      if (res.image_job_id) {
        setBriefImageStatus("generating");
        try {
          await postsService.pollMediaGenerationJob(
            res.post_id,
            res.image_job_id,
            "creative",
          );
          setBriefImageStatus("done");
        } catch (imgErr) {
          console.error("Erro ao gerar imagem do briefing:", imgErr);
          setBriefImageStatus("failed");
        } finally {
          onCreativeGenerated?.();
        }
      }
    } catch (err: any) {
      setBriefError(err?.message || t("Erro ao gerar post com Creative AI"));
    } finally {
      setBriefSubmitting(null);
    }
  };

  const handleGenerateVariations = async () => {
    if (!briefTopic.trim()) {
      setBriefError(t("Digite o tema do post"));
      return;
    }

    setBriefError(null);
    setBriefVariationsResult(null);
    setBriefSubmitting("variations");
    try {
      const platform = selectedPlatforms[0] || "instagram";
      const res = await postsService.generateBriefVariations({
        topic: briefTopic.trim(),
        product: briefProduct.trim() || undefined,
        objective: briefObjective.trim() || undefined,
        platform,
        format: briefFormat,
        num_variations: briefVariationsCount,
      });

      if (!res?.success || !res.creative_concept_id) {
        throw new Error(res?.error || t("Erro ao gerar variações"));
      }

      const variations = res.variations || [];
      const createdCount = variations.filter((v) => v.post_id).length;
      const reviewFlags = variations.flatMap((v) => v.review_flags || []);

      setBriefVariationsResult({
        conceptId: res.creative_concept_id,
        createdCount,
        total: variations.length,
        reviewFlags,
      });
      onCreativeGenerated?.();
    } catch (err: any) {
      setBriefError(err?.message || t("Erro ao gerar variações"));
    } finally {
      setBriefSubmitting(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting || loading) return;
    setAttemptedSubmit(true);

    // Validações
    if (!content.trim()) {
      setError(t("O conteúdo do post é obrigatório"));
      return;
    }

    if (selectedPlatforms.length === 0) {
      setError(t("Selecione pelo menos uma plataforma"));
      return;
    }

    // Validar galeria se necessário
    if (galleryItems.length === 0 && !content.trim()) {
      setError(t("Adicione ao menos uma mídia ou texto"));
      return;
    }

    // Se já gerou a imagem, não precisa gerar novamente
    if (generateImage && generatedImageUrl && tempPostId) {
      // Usar a imagem já gerada, não precisa gerar novamente
      // O tempPostId será usado para referenciar a imagem já gerada
    }

    // Prompt de vídeo é opcional (usa conteúdo do post se vazio)

    try {
      setIsSubmitting(true);
      setError(null);

      const firstImage = galleryItems.find((i) => i.type === "image")?.file;
      const firstVideo = galleryItems.find((i) => i.type === "video")?.file;

      let finalMediaGallery: any[] = [];
      if (galleryItems.length > 1) {
        finalMediaGallery = galleryItems.map((item) => ({
          file: item.file,
          type: item.type,
        }));
      }

      await onCreate({
        content: content.trim(),
        platforms: selectedPlatforms,
        hashtags: hashtags.trim() || undefined,
        call_to_action: callToAction.trim() || undefined,
        call_to_action_url: callToActionUrl.trim() || undefined,
        // Compatibilidade com single-media
        imageFile: firstImage || imageFile || undefined,
        videoFile: firstVideo || videoFile || undefined,
        mediaGallery: finalMediaGallery,
        videoDestination: galleryItems.some((i) => i.type === "video")
          ? videoDestination
          : undefined,
        videoCaption: galleryItems.some((i) => i.type === "video")
          ? videoCaption.trim() || undefined
          : undefined,
        videoHashtags: galleryItems.some((i) => i.type === "video")
          ? videoHashtags.trim() || undefined
          : undefined,
        generateImage: generateImage || undefined,
        imagePrompt: generateImage ? imagePrompt.trim() : undefined,
        imageProvider: generateImage ? imageProvider : undefined,
        generateVideo: generateVideo || undefined,
        videoPrompt: generateVideo ? videoPrompt.trim() : undefined,
        videoDuration: generateVideo ? videoDuration : undefined,
        videoProvider: generateVideo ? videoProvider : undefined,
        videoRestrictions: generateVideo
          ? videoRestrictions.trim() || undefined
          : undefined,
        videoLogoFile: generateVideo ? videoLogoFile || undefined : undefined,
        videoReferenceImages: generateVideo ? videoReferenceImages : undefined,
        videoTexts: generateVideo ? videoTexts : undefined,
        videoScript: generateVideo
          ? videoScript.trim() || undefined
          : undefined,
        enhanceWithAI: enhanceWithAI || undefined,
        tempPostId: tempPostId || undefined,
        generatedImageUrl: generatedImageUrl || undefined,
      });

      // Fechar modal após sucesso
      onClose();
    } catch (err: any) {
      // Preservar mensagem de erro específica do backend
      const errorMessage =
        err?.message ||
        err?.error ||
        err?.errorData?.error ||
        t("Erro ao criar post. Tente novamente.");
      console.error("❌ Erro ao criar post:", err);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Criar Novo Post")}
      subtitle={t(
        "Crie, agende e direcione publicações para suas redes sociais com IA",
      )}
      icon={<Plus className="h-5 w-5" />}
      headerVariant="gradient"
      maxWidth="max-w-4xl"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading || isSubmitting}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            icon={
              loading || isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )
            }
            disabled={
              loading ||
              isSubmitting ||
              !content.trim() ||
              selectedPlatforms.length === 0
            }
            form="create-post-form"
            className="shadow-lg shadow-purple-500/20"
          >
            {isSubmitting
              ? t("Criando...")
              : selectedPlatforms.length > 1
                ? t("Criar Posts")
                : t("Criar Post")}
          </Button>
        </div>
      }
    >
      <form
        id="create-post-form"
        onSubmit={handleSubmit}
        className="p-0.5 space-y-5"
      >
        {error && (
          <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-shake">
            <div className="shrink-0 mt-0.5">
              <svg
                className="h-4 w-4 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Seleção de Plataformas */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between px-1">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {t("Canais de Destino")} <span className="text-red-400">*</span>
            </label>
            <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
              {t("{count} Selecionados", { count: selectedPlatforms.length })}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PLATFORMS.map((platform: any) => {
              const isSelected = selectedPlatforms.includes(platform.id);
              const platformConfig = getPlatformConfig(platform.id);

              return (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() => handlePlatformToggle(platform.id)}
                  className={cn(
                    "flex items-center justify-center gap-2 px-3 py-2 rounded-xl border-2 transition-all duration-300 group",
                    "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1",
                    isSelected
                      ? "bg-purple-600 border-transparent text-white shadow-lg scale-[1.02]"
                      : attemptedSubmit && selectedPlatforms.length === 0
                        ? "bg-gray-50 border-red-300 text-gray-700 hover:border-red-400 hover:bg-red-50/50"
                        : "bg-gray-50 border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50/50",
                  )}
                >
                  <div
                    className={cn(
                      "p-1.5 rounded-lg border transition-colors",
                      isSelected
                        ? "bg-white/20 border-white/20"
                        : `${platformConfig.bgColor} ${platformConfig.borderColor}`,
                    )}
                  >
                    {React.cloneElement(
                      platform.icon as React.ReactElement<any>,
                      {
                        className: cn(
                          "h-4 w-4",
                          isSelected ? "text-white" : platformConfig.color,
                        ),
                      },
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-tight whitespace-nowrap">
                    {platform.name}
                  </span>
                  {isSelected && (
                    <CheckCircle className="h-3.5 w-3.5 text-white/80" />
                  )}
                </button>
              );
            })}
          </div>
          {attemptedSubmit && selectedPlatforms.length === 0 && (
            <p className="flex items-center gap-1 text-xs text-red-600 font-medium px-1">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {t("Selecione pelo menos uma plataforma")}
            </p>
          )}
        </div>

        {/* Creative AI — briefing simples (Estratégia -> Copy -> Criativo -> Revisor) */}
        <div className="rounded-2xl border border-indigo-200 bg-linear-to-br from-indigo-50/70 to-purple-50/70 overflow-hidden">
          <button
            type="button"
            onClick={() => setBriefPanelOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3"
          >
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-indigo-600 rounded-lg text-white shadow-xs">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <div className="text-left">
                <p className="text-xs font-bold text-indigo-900 uppercase tracking-tight leading-none">
                  {t("Gerar com Creative AI (Briefing)")}
                </p>
                <p className="text-[10px] text-indigo-500 font-medium mt-1">
                  {t(
                    "Tema + produto + objetivo → copy e imagem gerados e publicados como post pendente",
                  )}
                </p>
              </div>
            </div>
            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-tighter">
              {briefPanelOpen ? t("Fechar") : t("Abrir")}
            </span>
          </button>

          {briefPanelOpen && (
            <div className="px-4 pb-4 space-y-3 border-t border-indigo-100 pt-3.5">
              <div className="flex items-center gap-1.5">
                <Palette className="h-3 w-3 text-indigo-400" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-500">
                  {hasBrandKit
                    ? t("Brand Kit do cliente será aplicado (cores/fonte)")
                    : t("Nenhum Brand Kit configurado — usando estilo padrão")}
                </span>
              </div>

              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                  {t("Tema *")}
                </label>
                <Input
                  value={briefTopic}
                  onChange={(e) => setBriefTopic(e.target.value)}
                  placeholder={t(
                    "Ex: Seu time de marketing está sobrecarregado?",
                  )}
                  inputSize="sm"
                  disabled={briefSubmitting !== null}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                    {t("Produto/Serviço (opcional)")}
                  </label>
                  <Input
                    value={briefProduct}
                    onChange={(e) => setBriefProduct(e.target.value)}
                    placeholder={t("Ex: Virtus AI")}
                    inputSize="sm"
                    disabled={briefSubmitting !== null}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                    {t("Objetivo (opcional)")}
                  </label>
                  <Input
                    value={briefObjective}
                    onChange={(e) => setBriefObjective(e.target.value)}
                    placeholder={t("Ex: geração de leads")}
                    inputSize="sm"
                    disabled={briefSubmitting !== null}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                    {t("Gatilho emocional (opcional)")}
                  </label>
                  <Select
                    value={briefTrigger}
                    onChange={setBriefTrigger}
                    options={EMOTIONAL_TRIGGER_OPTIONS.map((opt) => ({
                      ...opt,
                      label: t(opt.label),
                    }))}
                    size="sm"
                    disabled={briefSubmitting !== null}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                    {t("Formato do criativo")}
                  </label>
                  <Select
                    value={briefFormat}
                    onChange={setBriefFormat}
                    options={CREATIVE_FORMAT_OPTIONS}
                    size="sm"
                    disabled={briefSubmitting !== null}
                  />
                </div>
              </div>

              {briefError && (
                <p className="flex items-center gap-1.5 text-xs text-red-600 font-medium px-1">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {briefError}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="flex-1 rounded-xl bg-indigo-600 shadow-md shadow-indigo-200"
                  onClick={handleGenerateFromBrief}
                  disabled={briefSubmitting !== null || !briefTopic.trim()}
                  icon={
                    briefSubmitting === "post" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )
                  }
                >
                  {briefSubmitting === "post"
                    ? t("Gerando...")
                    : t("Gerar Post")}
                </Button>

                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={2}
                    max={7}
                    value={briefVariationsCount}
                    onChange={(e) =>
                      setBriefVariationsCount(
                        Math.min(7, Math.max(2, Number(e.target.value) || 2)),
                      )
                    }
                    inputSize="sm"
                    className="w-16! text-center"
                    disabled={briefSubmitting !== null}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="flex-1 rounded-xl whitespace-nowrap"
                    onClick={handleGenerateVariations}
                    disabled={briefSubmitting !== null || !briefTopic.trim()}
                    icon={
                      briefSubmitting === "variations" ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Layers className="h-3.5 w-3.5" />
                      )
                    }
                  >
                    {briefSubmitting === "variations"
                      ? t("Gerando...")
                      : t("Gerar Variações")}
                  </Button>
                </div>
              </div>

              {briefResult && (
                <div className="p-3 rounded-xl bg-white border border-indigo-200 space-y-1.5">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-indigo-700">
                    <CheckCircle className="h-3.5 w-3.5" />
                    {t("Post #{id} criado — já aparece na lista de posts.", {
                      id: briefResult.postId,
                    })}
                  </p>
                  <p className="text-[10px] text-gray-500 font-medium">
                    {briefImageStatus === "generating" &&
                      t("Gerando imagem em segundo plano...")}
                    {briefImageStatus === "done" &&
                      t("Imagem gerada com sucesso.")}
                    {briefImageStatus === "failed" &&
                      t(
                        "Falha ao gerar a imagem — o post foi criado, gere manualmente pela lista.",
                      )}
                  </p>
                  {briefResult.reviewFlags.length > 0 && (
                    <div className="space-y-1 pt-1">
                      {briefResult.reviewFlags.map((flag, i) => (
                        <p
                          key={i}
                          className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1"
                        >
                          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                          {flag}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {briefVariationsResult && (
                <div className="p-3 rounded-xl bg-white border border-indigo-200 space-y-1.5">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-indigo-700">
                    <CheckCircle className="h-3.5 w-3.5" />
                    {t(
                      "{count} de {total} variações criadas — já aparecem na lista de posts.",
                      {
                        count: briefVariationsResult.createdCount,
                        total: briefVariationsResult.total,
                      },
                    )}
                  </p>
                  <p className="text-[10px] text-gray-500 font-mono">
                    creative_concept_id: {briefVariationsResult.conceptId}
                  </p>
                  {briefVariationsResult.reviewFlags.length > 0 && (
                    <div className="space-y-1 pt-1">
                      {briefVariationsResult.reviewFlags.map((flag, i) => (
                        <p
                          key={i}
                          className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1"
                        >
                          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                          {flag}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Conteúdo do Post */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {t("Legenda do Post")} <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-3">
              <CharCounter
                current={content.length}
                recommended={captionLimit?.recommended}
                hard={captionLimit?.hard}
              />
              {enhanceWithAI && (
                <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {t("Melhorado")}
                </span>
              )}
            </div>
          </div>

          <div className="relative">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              error={
                attemptedSubmit && !content.trim()
                  ? t("A legenda é obrigatória")
                  : undefined
              }
              placeholder={t("Escreva o conteúdo do seu post aqui...")}
              className={cn(
                "p-4 pb-12 rounded-2xl! focus:border-purple-500! text-sm bg-gray-50! focus:bg-white! placeholder:text-gray-400! min-h-[130px]! leading-relaxed shadow-inner",
                attemptedSubmit && !content.trim()
                  ? "border-red-300!"
                  : "border-purple-200!",
              )}
            />
            <div className="absolute bottom-3 right-3">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleEnhanceContent}
                disabled={enhancing || !content.trim()}
                icon={
                  enhancing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Wand2 className="h-3 w-3" />
                  )
                }
                className="rounded-xl shadow-md shadow-purple-500/10"
              >
                {enhancing ? t("Processando...") : t("Melhorar com IA")}
              </Button>
            </div>
          </div>
        </div>

        {/* Hashtags, CTA e Link — sempre visíveis, não escondidos atrás de aba */}
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                {t("Hashtags Estratégicas (opcional)")}
              </label>
              {hashtagLimit !== undefined ? (
                <CharCounter
                  current={hashtagsArray.length}
                  hard={hashtagLimit}
                />
              ) : (
                <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-purple-200">
                  {t("{count} Ativas", { count: hashtagsArray.length })}
                </span>
              )}
            </div>

            {/* Badges das hashtags */}
            {hashtagsArray.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 bg-white p-3 rounded-xl border border-purple-200 min-h-[48px]">
                {hashtagsArray.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-purple-200 group transition-all hover:bg-indigo-100"
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => removeHashtag(tag)}
                      className="text-indigo-300 hover:text-red-500 hover:bg-white rounded-full p-0.5 transition-all focus:outline-hidden"
                    >
                      <Plus className="h-3.5 w-3.5 rotate-45" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative group">
              <Input
                type="text"
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyDown={handleHashtagInputKeyDown}
                onBlur={handleHashtagInputBlur}
                placeholder={t("Digite e pressione Enter...")}
                inputSize="sm"
                className="shadow-xs group-hover:shadow-md"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <kbd className="hidden sm:inline-flex px-1.5 py-0.5 text-[9px] font-bold bg-gray-100 text-gray-400 rounded-sm border border-purple-200 uppercase tracking-tighter">
                  Enter
                </kbd>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 font-medium px-1 italic">
              {t(
                "O sistema irá agrupar as hashtags para melhor performance SEO.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
                {t("Chamada para Ação — CTA (opcional)")}
              </label>
              <Input
                type="text"
                value={callToAction}
                onChange={(e) => setCallToAction(e.target.value)}
                placeholder={t("Ex: Saiba Mais, Link na Bio...")}
                inputSize="sm"
                className="border-purple-200!"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
                {t("Link de Destino (opcional)")}
              </label>
              <Input
                type="url"
                value={callToActionUrl}
                onChange={(e) => setCallToActionUrl(e.target.value)}
                placeholder="https://suapagina.com.br"
                inputSize="sm"
                className="border-purple-200!"
              />
            </div>
          </div>

          <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-xl bg-linear-to-br from-indigo-900 to-purple-900 text-white shadow-xs">
            <Sparkles className="h-3.5 w-3.5 text-purple-300 shrink-0 mt-0.5" />
            <p className="text-[11px] leading-snug text-purple-100 font-medium">
              <span className="font-bold uppercase tracking-wider text-purple-200 text-[10px]">
                {t("Dica:")}
              </span>{" "}
              {t(
                'CTAs claros convertem até 28% mais. Use verbos de ação como "Descubra", "Garanta" ou "Participe".',
              )}
            </p>
          </div>
        </div>

        {/* Tabs para Mídia e Pré-visualização */}
        <div className="w-full">
          <div
            role="group"
            aria-label={t("Alternar entre Mídia e Pré-visualização")}
            className="mb-3 sm:mb-4 flex w-full items-center gap-1 p-1 bg-gray-100 rounded-xl"
          >
            <button
              type="button"
              onClick={() => setActiveTab("media")}
              aria-pressed={activeTab === "media"}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all",
                activeTab === "media"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-700",
              )}
            >
              <ImageIcon className="h-4 w-4" />
              {t("Mídia")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("preview")}
              aria-pressed={activeTab === "preview"}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all",
                activeTab === "preview"
                  ? "bg-purple-600 text-white shadow-xs"
                  : "text-gray-500 hover:text-gray-700",
              )}
            >
              <Eye className="h-4 w-4" />
              {t("Pré-visualização")}
            </button>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="hidden">
              <TabsTrigger value="media">{t("Mídia")}</TabsTrigger>
              <TabsTrigger value="preview">{t("Pré-visualização")}</TabsTrigger>
            </TabsList>

            <TabsContent
              value="media"
              className="space-y-4 mt-4 focus:outline-hidden"
            >
              {/* Galeria de Conteúdo (Carrossel/Álbum) */}
              <div className="space-y-4 p-4 rounded-2xl bg-linear-to-br from-purple-50/50 to-indigo-50/50 border border-purple-200 shadow-xs transition-all hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-purple-600 rounded-xl text-white shadow-lg shadow-purple-600/20">
                      <ImageIcon className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight leading-none">
                        {t("Galeria de Mídias")}
                      </h3>
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                        {t("Carrossel • Álbum • Múltiplos Arquivos")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-tighter shadow-xs",
                        galleryItems.length > 0
                          ? "bg-purple-600 text-white border-transparent"
                          : "bg-white text-purple-600 border-purple-200",
                      )}
                    >
                      {t("{count} / 10 Itens", { count: galleryItems.length })}
                    </span>
                  </div>
                </div>

                {/* Grid de Previews */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {galleryItems.map((item, index) => (
                    <div
                      key={index}
                      className="relative group animate-in fade-in zoom-in duration-300"
                    >
                      <div className="relative aspect-square rounded-2xl overflow-hidden border-2 border-purple-100 bg-white shadow-xs group-hover:border-purple-300 transition-all">
                        {item.type === "image" ? (
                          <img
                            src={item.preview}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 gap-2">
                            <Video className="h-8 w-8 text-purple-400" />
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              {t("Vídeo")}
                            </span>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg hover:scale-110 active:scale-95"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>

                        {/* Badge de Variações se existirem */}
                        {item.variations && (
                          <button
                            type="button"
                            onClick={() => openVariations(index)}
                            className="absolute bottom-2 left-2 px-2 py-0.5 bg-green-500 hover:bg-green-600 text-white text-[9px] font-black uppercase rounded-full shadow-lg flex items-center gap-1 transition-colors"
                          >
                            <Sparkles className="h-2 w-2" />
                            {t("Smart Resized")}
                          </button>
                        )}

                        {/* Badge de Ordem */}
                        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/60 backdrop-blur-xs rounded-md text-[9px] font-bold text-white border border-white/20">
                          #{index + 1}
                        </div>
                      </div>

                      {/* Display de Variações Sociais */}
                      {item.variations && (
                        <div className="mt-2 flex flex-wrap gap-1.5 px-1">
                          {Object.keys(item.variations).map((key) => {
                            const labels: Record<string, string> = {
                              square: "1:1",
                              portrait: "4:5",
                              stories: "9:16",
                              landscape: "1.91:1",
                            };
                            return (
                              <div
                                key={key}
                                className="px-1.5 py-0.5 bg-gray-100 rounded-sm text-[8px] font-bold text-gray-500 uppercase border border-gray-200"
                                title={labels[key] || key}
                              >
                                {labels[key] || key}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <p className="mt-1.5 text-[10px] font-medium text-gray-500 truncate px-1">
                        {item.file.name === "IA_Generated.png"
                          ? t("Gerado por IA")
                          : item.file.name}
                      </p>
                    </div>
                  ))}

                  {/* Slots de Adição */}
                  {galleryItems.length < 10 && (
                    <>
                      <label className="cursor-pointer group flex flex-col items-center justify-center p-3 rounded-2xl border-2 border-dashed border-purple-200 bg-white/40 hover:border-purple-500 hover:bg-white transition-all aspect-square shadow-xs duration-300">
                        <div className="p-2 bg-purple-50 rounded-xl group-hover:bg-purple-600 transition-colors duration-300 group-hover:text-white text-purple-600 shadow-inner">
                          <Plus className="h-5 w-5" />
                        </div>
                        <span className="mt-2 text-[9px] font-bold text-gray-500 uppercase tracking-tighter group-hover:text-purple-600">
                          {t("Add Arquivos")}
                        </span>
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            files.forEach((file) => {
                              const type = file.type.startsWith("image/")
                                ? "image"
                                : "video";
                              if (type === "image") {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setGalleryItems((prev) => [
                                    ...prev,
                                    {
                                      file,
                                      preview: reader.result as string,
                                      type: "image",
                                    },
                                  ]);
                                };
                                reader.readAsDataURL(file);
                              } else {
                                setGalleryItems((prev) => [
                                  ...prev,
                                  { file, preview: "", type: "video" },
                                ]);
                              }
                            });
                          }}
                          className="hidden"
                          disabled={isSubmitting}
                        />
                      </label>

                      {!generateVideo && (
                        <button
                          type="button"
                          onClick={() => setGenerateImage(true)}
                          className="group flex flex-col items-center justify-center p-3 rounded-2xl border-2 border-dashed border-indigo-200 bg-white/40 hover:border-indigo-500 hover:bg-white transition-all aspect-square shadow-xs duration-300"
                        >
                          <div className="p-2 bg-indigo-50 rounded-xl group-hover:bg-indigo-600 transition-colors duration-300 group-hover:text-white text-indigo-600 shadow-inner">
                            <Sparkles className="h-5 w-5" />
                          </div>
                          <span className="mt-2 text-[9px] font-bold text-gray-500 uppercase tracking-tighter group-hover:text-indigo-600">
                            {t("Gerar Imagem IA")}
                          </span>
                        </button>
                      )}

                      {!generateImage && (
                        <button
                          type="button"
                          onClick={() => setGenerateVideo(true)}
                          className="group flex flex-col items-center justify-center p-3 rounded-2xl border-2 border-dashed border-blue-200 bg-white/40 hover:border-blue-500 hover:bg-white transition-all aspect-square shadow-xs duration-300"
                        >
                          <div className="p-2 bg-blue-50 rounded-xl group-hover:bg-blue-600 transition-colors duration-300 group-hover:text-white text-blue-600 shadow-inner">
                            <Video className="h-5 w-5" />
                          </div>
                          <span className="mt-2 text-[9px] font-bold text-gray-500 uppercase tracking-tighter group-hover:text-blue-600">
                            {t("Gerar Vídeo IA")}
                          </span>
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Configurações do vídeo enviado na galeria (destino, legenda e
                    hashtags específicas) — só relevante quando há um vídeo real
                    (arquivo enviado), não se aplica ao vídeo gerado por IA. */}
                {galleryItems.some((i) => i.type === "video") && (
                  <div className="p-4 bg-white/95 rounded-2xl border border-purple-100 shadow-xs space-y-3">
                    <div className="flex items-center gap-2">
                      <Video className="h-3.5 w-3.5 text-purple-500" />
                      <span className="text-[10px] font-extrabold text-purple-700 uppercase tracking-wider">
                        {t("Configurações do Vídeo")}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                        {t("Destino do Vídeo")}
                      </label>
                      <Select
                        value={videoDestination}
                        onChange={setVideoDestination}
                        options={VIDEO_DESTINATION_OPTIONS.map((opt) => ({
                          ...opt,
                          label: t(opt.label),
                        }))}
                        size="sm"
                        disabled={isSubmitting || loading}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                          {t("Legenda do Vídeo (opcional)")}
                        </label>
                        <Input
                          placeholder={t(
                            "Se vazio, usa a legenda do post acima",
                          )}
                          value={videoCaption}
                          onChange={(e) => setVideoCaption(e.target.value)}
                          inputSize="sm"
                          disabled={isSubmitting || loading}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                          {t("Hashtags do Vídeo (opcional)")}
                        </label>
                        <Input
                          placeholder={t("#exemplo, #outra")}
                          value={videoHashtags}
                          onChange={(e) => setVideoHashtags(e.target.value)}
                          inputSize="sm"
                          disabled={isSubmitting || loading}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Painel IA Gerativa */}
                {generateImage && (
                  <div className="mt-4 p-4 bg-white/95 backdrop-blur-md rounded-2xl border border-indigo-100 shadow-xl animate-fade-in">
                    {!generatingImage ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between border-b border-indigo-50 pb-2">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
                            <span className="text-[10px] font-extrabold text-indigo-700 uppercase tracking-wider">
                              {t("Criador de Arte IA")}
                            </span>
                          </div>
                          <button
                            onClick={() => setGenerateImage(false)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                            {t("Provedor de IA")}
                          </label>
                          <Select
                            options={[...IMAGE_PROVIDER_OPTIONS]}
                            value={imageProvider}
                            onChange={setImageProvider}
                            size="sm"
                          />
                          <p className="text-xs text-gray-500">
                            {imageProviderHint(imageProvider) &&
                              t(imageProviderHint(imageProvider))}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                            {t("Inspiração Visual")}
                          </label>
                          <Input
                            placeholder={t(
                              "Ex: Um pôr do sol futurista sobre uma metrópole neon...",
                            )}
                            value={imagePrompt}
                            onChange={(e) => setImagePrompt(e.target.value)}
                            inputSize="sm"
                            className="bg-gray-50/50!"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            className="flex-1 rounded-xl bg-indigo-600 shadow-lg shadow-indigo-200"
                            onClick={handleGenerateImagePreview}
                            disabled={!imagePrompt.trim()}
                          >
                            {t("Gerar e Adicionar à Galeria")}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="flex-1 rounded-xl"
                            onClick={() => setGenerateImage(false)}
                          >
                            {t("Cancelar")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 flex flex-col items-center justify-center space-y-4 animate-pulse">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                        <div className="text-center">
                          <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest leading-none">
                            {t("A IA está pintando seu prompt...")}
                          </p>
                          <p className="text-[9px] text-gray-400 mt-2 font-mono uppercase tracking-tighter">
                            {t("Aguarde 15s")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {generateVideo && (
                  <div className="mt-4 p-4 bg-white/95 backdrop-blur-md rounded-2xl border border-blue-100 shadow-xl animate-fade-in">
                    <div className="flex items-center justify-between border-b border-blue-50 pb-2 mb-4">
                      <div className="flex items-center gap-2">
                        <Video className="h-3.5 w-3.5 text-blue-500" />
                        <span className="text-[10px] font-extrabold text-blue-700 uppercase tracking-wider">
                          {t("Criador de Vídeo IA")}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setGenerateVideo(false)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <VideoGenerationFields
                      compact
                      disabled={isSubmitting || loading}
                      videoPrompt={videoPrompt}
                      setVideoPrompt={setVideoPrompt}
                      videoDuration={videoDuration}
                      setVideoDuration={setVideoDuration}
                      videoRestrictions={videoRestrictions}
                      setVideoRestrictions={setVideoRestrictions}
                      videoProvider={videoProvider}
                      setVideoProvider={setVideoProvider}
                      logoFile={videoLogoFile}
                      setLogoFile={setVideoLogoFile}
                      logoPreview={videoLogoPreview}
                      setLogoPreview={setVideoLogoPreview}
                      referenceImages={videoReferenceImages}
                      setReferenceImages={setVideoReferenceImages}
                      referenceImagePreviews={videoReferencePreviews}
                      setReferenceImagePreviews={setVideoReferencePreviews}
                      videoTexts={videoTexts}
                      setVideoTexts={setVideoTexts}
                      videoScript={videoScript}
                      setVideoScript={setVideoScript}
                    />
                    <p className="text-xs text-gray-500 mt-4">
                      {t(
                        "O vídeo será gerado após criar o post (pode levar alguns minutos).",
                      )}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent
              value="preview"
              className="space-y-4 mt-4 focus:outline-hidden"
            >
              {selectedPlatforms.length === 0 ? (
                <div className="text-center py-10 text-xs text-gray-400 font-medium">
                  {t(
                    "Selecione ao menos uma plataforma para ver a pré-visualização.",
                  )}
                </div>
              ) : (
                <>
                  {selectedPlatforms.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-2">
                      {selectedPlatforms.map((id) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setPreviewPlatformOverride(id)}
                          className={cn(
                            "px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all",
                            id === previewPlatform
                              ? "bg-purple-600 text-white border-transparent shadow-xs"
                              : "bg-white text-gray-500 border-purple-200 hover:bg-purple-50",
                          )}
                        >
                          {getPlatformName(id)}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="max-w-sm mx-auto bg-white border border-purple-200 rounded-2xl shadow-xs overflow-hidden">
                    <div className="p-3 flex items-center gap-2.5 border-b border-gray-100">
                      {previewAccountLogo ? (
                        <img
                          src={previewAccountLogo}
                          alt=""
                          className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-linear-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xs font-black shrink-0">
                          {previewAccountName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-gray-900 truncate">
                          {previewAccountName}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {getPlatformName(previewPlatform)} • {t("Agora")}
                        </p>
                      </div>
                    </div>

                    {galleryItems[0] && (
                      <div className="w-full aspect-square bg-gray-100">
                        {galleryItems[0].type === "image" ? (
                          <img
                            src={galleryItems[0].preview}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="h-10 w-10 text-gray-300" />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="p-3.5 space-y-2">
                      <p className="text-xs text-gray-800 whitespace-pre-wrap wrap-break-word leading-relaxed">
                        {content || (
                          <span className="italic text-gray-300">
                            {t("Sua legenda vai aparecer aqui...")}
                          </span>
                        )}
                      </p>
                      {hashtagsArray.length > 0 && (
                        <p className="text-xs text-blue-600 wrap-break-word">
                          {hashtagsArray.map((tag) => `#${tag}`).join(" ")}
                        </p>
                      )}
                      {callToAction && (
                        <div className="pt-2">
                          <span className="inline-block px-3 py-1.5 rounded-lg bg-purple-600 text-white text-[11px] font-bold">
                            {callToAction}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {galleryItems.length > 1 && (
                    <p className="text-center text-[10px] text-gray-400 font-medium">
                      {t("+ {count} mídias no carrossel", {
                        count: galleryItems.length - 1,
                      })}
                    </p>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </form>

      {selectedItemForVariations && (
        <SocialVariationsModal
          open={variationsModalOpen}
          onClose={() => setVariationsModalOpen(false)}
          variations={selectedItemForVariations.variations}
          originalUrl={selectedItemForVariations.originalUrl}
        />
      )}
    </Modal>
  );
};

export default CreatePostModal;
